import type { TailorStage } from "@/lib/applications";
import { TailorButton } from "@/components/site/PostingActions";
import type { WrittenResume } from "@/lib/posting";
import ResumesWritten from "./ResumesWritten";

const LINE: Record<TailorStage, string> = {
    none: "Create a version tailored to this role using your existing experience.",
    tailoring:
        "Writing a version for this role from your existing experience. It will appear here.",
    tailored: "Written for this role from your existing experience.",
};

/**
 * The person's resume for this posting, in one place: Tailor my resume, with
 * the model that writes it and what it costs, then what was written. Nothing
 * here adds to the resume: a tailor uses only what it already states.
 */
export default function TailorCard({
    postingId,
    job,
    tailor,
    written,
}: {
    postingId: string;
    /** What the tailor's dialog names the posting by. */
    job: { title: string; company?: string | null };
    /** Tailor my resume and its terms, or null when it is not offered. */
    tailor: {
        stage: TailorStage;
        model: string;
        cost: string | null;
        resumeId?: string | null;
        failed?: boolean;
    } | null;
    written: readonly WrittenResume[];
}) {
    if (!tailor && written.length === 0) return null;
    return (
        <section
            aria-labelledby="tailor-card"
            className="overflow-hidden rounded-card border border-line bg-card shadow-card"
        >
            <div className="flex flex-col gap-4 p-6 max-[760px]:p-5">
                <div className="flex flex-col gap-1.5">
                    <h2
                        id="tailor-card"
                        className="m-0 font-display text-card-title font-bold tracking-[-0.01em]"
                    >
                        Your resume for this job
                    </h2>
                    <p className="m-0 text-ui text-fg-muted [text-wrap:pretty]">
                        {LINE[tailor?.stage ?? "tailored"]}
                    </p>
                </div>
                {tailor && (
                    <TailorButton
                        postingId={postingId}
                        job={job}
                        failed={tailor.failed}
                        queued={tailor.stage === "tailoring"}
                        tailored={tailor.stage === "tailored"}
                        resumeId={tailor.resumeId}
                        size="wide"
                    />
                )}
                {written.length > 0 && <ResumesWritten written={written} />}
            </div>
            {tailor?.stage === "none" && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line bg-card-raised px-6 py-3 text-small text-fg-muted max-[760px]:px-5">
                    <span>
                        Model <span className="ml-1 font-semibold text-fg">{tailor.model}</span>
                    </span>
                    {tailor.cost && <span>{tailor.cost}</span>}
                </div>
            )}
        </section>
    );
}
